import React from 'react'
import { Link } from 'react-router-dom'
import { Card } from '../components/ui'
import type { AdminHubMetaProps, User } from '../data/types'
import { AnnouncementIcon, CalendarIcon, ChartIcon, ClipboardCheckIcon, ClipboardIcon, ColorIcon, DocumentIcon, EmailIcon, FlagIcon, GraduationCapIcon, InternationalIcon, LogIcon, OnlineIcon, ScanIcon, SettingsIcon, ThumbsUpIcon, UsersIcon } from '../icons'
import { useAuth } from './auth'

type Props = {
    meta: AdminHubMetaProps
}

type SectionLink = {
    title: string
    description: string
    path: string
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
    color: 'primary' | 'success' | 'warn' | 'danger'
    show?: (user: User) => boolean
}

export const AdminHub = (props: Props) => {
    const { meta } = props

    const sections: { title: string; links: SectionLink[] }[] = [
        /** Overview */
        {
            title: 'Overview',
            links: [

                {
                    title: meta.miTitle,
                    description: meta.miDescription,
                    path: '/management-analytics',
                    icon: ChartIcon,
                    color: 'primary',
                    show: (user) => user.permissions?.includes('view_stats')
                },

                {
                    title: meta.eventDayTitle,
                    description: meta.eventDayDescription,
                    path: '/live-stats',
                    icon: OnlineIcon,
                    color: 'primary',
                    show: (user) => user.permissions?.includes('view_live_stats')
                }
            ]
        },
        /** Event Management */
        {
            title: 'Event Management',
            links: [
                {
                    title: meta.eventsTitle,
                    description: meta.eventsDescription,
                    path: '/events',
                    icon: CalendarIcon,
                    color: 'success',
                    show: (user) => user.permissions?.includes('view_events')
                },
                {
                    title: meta.dutiesTitle,
                    description: meta.dutiesDescription,
                    path: '/duty',
                    icon: ClipboardIcon,
                    color: 'success',
                    show: (user) => user.permissions?.includes('manage_duties')
                },
                {
                    title: meta.allocationStatusTitle,
                    description: meta.allocationStatusDescription,
                    path: '/admin/allocation-status',
                    icon: ClipboardCheckIcon,
                    color: 'success',
                    show: (user) => user.permissions?.includes('view_allocation_status')
                },
                {
                    title: meta.checkInTitle,
                    description: meta.checkInDescription,
                    path: '/admin/check-in',
                    icon: ScanIcon,
                    color: 'success',
                    show: (user) => user.permissions?.includes('manage_check_ins')
                },
                {
                    title: meta.accessColorTitle,
                    description: meta.accessColorDescription,
                    path: '/access-color',
                    icon: ColorIcon,
                    color: 'success',
                    show: (user) => user.permissions?.includes('manage_access_colors')
                }
            ]
        },
        /** Volunteer Management */
        {
            title: 'Volunteer Management',
            links: [
                {
                    title: meta.volunteersTitle,
                    description: meta.volunteersDescription,
                    path: '/volunteer',
                    icon: UsersIcon,
                    color: 'warn',
                    show: (user) => user.permissions?.includes('list_volunteers')
                },
                {
                    title: meta.directorVolunteerTitle,
                    description: meta.directorVolunteerDescription,
                    path: '/director/volunteer',
                    icon: UsersIcon,
                    color: 'warn',
                    show: (user) => user.permissions?.includes('view_director_volunteers')
                },
                {
                    title: meta.trainingsTitle,
                    description: meta.trainingsDescription,
                    path: '/trainings',
                    icon: GraduationCapIcon,
                    color: 'warn',
                    show: (user) => user.permissions?.includes('manage_trainings')
                },
                // {
                //     title: meta.guidelineTitle,
                //     description: meta.guidelineDescription,
                //     path: '/guidelines',
                //     icon: GraduationCapIcon,
                //     color: 'warn',
                //     show: (user) => user.permissions?.includes('manage_guidelines')
                // },
                {
                    title: meta.preferencesTitle,
                    description: meta.preferencesDescription,
                    path: '/preferences',
                    icon: ThumbsUpIcon,
                    color: 'warn',
                    show: (user) => user.permissions?.includes('manage_preferences')
                },
                {
                    title: meta.flagsTitle,
                    description: meta.flagsDescription,
                    path: '/flags',
                    icon: FlagIcon,
                    color: 'warn',
                    show: (user) => user.permissions?.includes('manage_flags')
                },
                {
                    title: meta.announcementsTitle,
                    description: meta.announcementsDescription,
                    path: '/announcements',
                    icon: AnnouncementIcon,
                    color: 'warn',
                    show: (user) => user.permissions?.includes('manage_announcements')
                },
                {
                    title: meta.emailCampaignsTitle,
                    description: meta.emailCampaignsDescription,
                    path: '/admin/email-campaigns',
                    icon: EmailIcon,
                    color: 'warn',
                    show: (user) => user.permissions?.includes('manage_email_campaigns') || user.permissions?.includes('approve_email_campaigns')
                },
                {
                    title: meta.formsTitle,
                    description: meta.formsDescription,
                    path: '/admin/forms',
                    icon: DocumentIcon,
                    color: 'warn',
                    show: (user) => user.permissions?.includes('manage_forms')
                },
                {
                    title: meta.internationalRegisterationTitle,
                    description: meta.internationalRegisternationDescription,
                    path: '/international-registerations',
                    icon: InternationalIcon,
                    color: 'warn',
                    show: (user) => user.permissions?.includes('list_volunteers')
                },
                {
                    title: meta.JvTitle,
                    description: meta.JVDescription,
                    path: '/jv-points',
                    color: 'warn',
                    icon: GraduationCapIcon,
                    show: (user) => user.permissions?.includes('manage_jv_points')
                }
            ]
        },
        /** System */
        {
            title: 'System',
            links: [
                {
                    title: meta.userTitle,
                    description: meta.userDescription,
                    path: '/user',
                    icon: UsersIcon,
                    color: 'danger',
                    show: (user) => user.permissions?.includes('manage_users')
                },
                {
                    title: meta.settingsTitle,
                    description: meta.settingsDescription,
                    path: '/admin/settings',
                    icon: SettingsIcon,
                    color: 'danger',
                    show: (user) => user.permissions?.includes('manage_settings')
                },
                {
                    title: meta.userLogTitle,
                    description: meta.userLogDescription,
                    path: '/admin/user-log',
                    icon: LogIcon,
                    color: 'danger',
                    show: (user) => user.permissions?.includes('view_user_logs')
                }
            ]
        }
    ]

    const { user } = useAuth()

    sections.forEach(section => {
        section.links = section.links.filter(link => !link.show || link.show(user))
    })

    const visibleSections = sections.filter(section => section.links.length > 0)

    return (
        <div className="flex-1 container mx-auto px-4 py-8">
            <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                    {meta.title}
                </h1>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                    {meta.description}
                </p>
            </div>

            <div className="space-y-12">
                {visibleSections.map((section) => (
                    <div key={section.title}>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                            {section.title}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {section.links.map((link) => {
                                if (link.show && !link.show(user)) {
                                    return null
                                }
                                const Icon = link.icon
                                const colorClasses = {
                                    primary: 'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-300',
                                    success: 'bg-success-100 text-success-600 dark:bg-success-900 dark:text-success-300',
                                    warn: 'bg-warn-100 text-warn-600 dark:bg-warn-900 dark:text-warn-300',
                                    danger: 'bg-danger-100 text-danger-600 dark:bg-danger-900 dark:text-danger-300'
                                }

                                return (
                                    <Link key={link.path} to={link.path}>
                                        <Card className="h-full hover:shadow-lg transition-shadow duration-200 cursor-pointer group">
                                            <div className="p-6">
                                                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${colorClasses[link.color as keyof typeof colorClasses]}`}>
                                                    <Icon className="w-6 h-6" />
                                                </div>
                                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                                    {link.title}
                                                </h3>
                                                <p className="text-gray-600 dark:text-gray-400">
                                                    {link.description}
                                                </p>
                                            </div>
                                        </Card>
                                    </Link>
                                )
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}
